import { Injectable } from "@angular/core";
import { HttpContextToken } from "@angular/common/http";
import {
  createCounter,
  onPendingWorkChange,
  formatPendingWorkDetail,
  Counter,
} from "@apexdesigner/pending-work";

/**
 * Pending Work
 *
 * Client-side surface for the pending-work registry. Reflects the
 * aggregate busy state onto the DOM (`data-ad-busy` /
 * `data-ad-busy-detail` on body) so the e2e `settled(page)` fixture can
 * wait for in-flight requests and pending debounced autosaves, and
 * exposes the HttpContext token that opts a request out of counting.
 */
@Injectable({ providedIn: "root" })
export class PendingWorkService {
  /** Exclude - HttpContext token that opts a request out of pending-work counting. Set it on perpetual traffic (polling, health checks) — otherwise the app never reads as settled */
  exclude: HttpContextToken<boolean> = new HttpContextToken<boolean>(
    () => false,
  );

  /** Http Counter - Pending-work contributor for in-flight HTTP requests, used by the pending-work interceptor */
  httpCounter: Counter = createCounter("http");
  constructor() {
    this.initialize();
  }

  /** Initialize - Reflect the pending-work aggregate onto the body for the e2e settled fixture */ initialize() {
    /**
     * Mirror every pending-work change onto the body element
     *
     * The e2e settled(page) fixture polls the DOM, so the registry's state
     * has to be readable from outside the app.
     */
    onPendingWorkChange((snapshot) => {
      if (snapshot.busy) {
        // Mark the app busy and describe what it is waiting on
        document.body.setAttribute("data-ad-busy", "");
        document.body.setAttribute(
          "data-ad-busy-detail",
          formatPendingWorkDetail(snapshot),
        );
      }
      // Nothing is outstanding
      else {
        // Clear both markers
        document.body.removeAttribute("data-ad-busy");
        document.body.removeAttribute("data-ad-busy-detail");
      }
    });
  }
}
